import type { ResolvedComponent } from "@inertiajs/react"
import { createInertiaApp, router } from "@inertiajs/react"
import { StrictMode } from "react"
import { createRoot, hydrateRoot } from "react-dom/client"
import { I18nextProvider } from "react-i18next"

import { initializeTheme } from "@/hooks/use-appearance"
import PersistentLayout from "@/layouts/persistent-layout"
import i18n, { initI18n, syncI18nLocale } from "@/lib/i18n/client"
import type { Locale } from "@/types"

import { handleNewRelicNavigate } from "./newrelic-navigate"
import {
  type PostHogBootstrap,
  initPostHog,
  syncPostHogIdentity,
} from "./posthog-analytics"

// OKS-266 — hardcoded "OKS Crew" per PRD §5 + TRD KD-8 (Crew is OKS-
// branded, not white-label; the env-var indirection was starter-kit tech
// debt that obscured the product name in code review). The title callback
// below produces "<page> - OKS Crew" for every Inertia navigation, matching
// the layout's `<title inertia>` default.
const appName = "OKS Crew"

function readInitialLocale(initialPage: {
  props?: unknown
}): Locale | undefined {
  const props = (initialPage.props ?? {}) as { locale?: unknown }
  const raw = props.locale
  return raw === "en" || raw === "fr" ? raw : undefined
}

// OKS-533 — Pull the dedicated `posthog` shared prop off a page payload.
// Returns `null` (→ SDK no-op) when the prop is absent/nil (no key or no
// authenticated user). Kept narrow on purpose: we read ONLY this prop, so
// the analytics module never touches the PII-carrying `currentUser` prop.
function readPostHogConfig(page: { props?: unknown }): PostHogBootstrap | null {
  const props = (page.props ?? {}) as { posthog?: unknown }
  const raw = props.posthog
  return raw ? (raw as PostHogBootstrap) : null
}

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  title: (title) => (title ? `${title} - ${appName}` : appName),

  resolve: (name) => {
    // NB: excluding `__tests__` + `*.test.tsx` / `*.spec.tsx` from the glob is
    // load-bearing — Vite's production bundle would otherwise pull vitest into
    // the browser entry. See OKS-5 P0 (QA review).
    const pages = import.meta.glob<{ default: ResolvedComponent }>(
      [
        "../pages/**/*.tsx",
        "!../pages/**/__tests__/**",
        "!../pages/**/*.test.tsx",
        "!../pages/**/*.spec.tsx",
      ],
      {
        eager: true,
      },
    )
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    page.default.layout ??= [PersistentLayout]

    return page
  },

  setup({ el, App, props }) {
    // Locale is server-driven via the root `locale` shared prop (Backend B2).
    // Init i18next BEFORE render so the first paint is already localised.
    const initialLocale = readInitialLocale(props.initialPage)
    initI18n({ locale: initialLocale })

    // OKS-533 — Initialise PostHog from the initial page's `posthog` prop.
    // No-op when the prop is null (no key / anonymous surface); mirrors the
    // guarded New Relic wiring below. Init is idempotent, so the navigate
    // hook can safely re-attempt it after a sign-in flips the prop non-null.
    initPostHog(readPostHogConfig(props.initialPage))

    // Keep i18next in sync across SPA navigations — the root `locale` prop can
    // change (sign-in switches anon FR → authed EN, sign-out goes back to FR).
    // The Inertia router emits `success` after each page load with the
    // resolved page payload; tap it to pull the fresh locale.
    router.on("success", (event) => {
      const rootProps = (event.detail.page.props ?? {}) as { locale?: unknown }
      const next = rootProps.locale
      if (next === "en" || next === "fr") {
        syncI18nLocale(next)
      }
    })

    // OKS-310 — Report each Inertia SPA navigation to New Relic Browser as a
    // named route change (guarded no-op when the agent is off; see
    // ./newrelic-navigate). Additive to the `success` handler above.
    //
    // OKS-533 — On each navigation, re-attempt init (covers the anon→authed
    // transition where the `posthog` prop flips non-null after sign-in) and
    // reconcile the identified user + brand group. Both are guarded no-ops
    // without a key, and `syncPostHogIdentity` only fires identify/group on
    // an actual identity change (anti-storm, KD-7).
    router.on("navigate", (event) => {
      handleNewRelicNavigate(event.detail.page)

      const posthogConfig = readPostHogConfig(event.detail.page)
      initPostHog(posthogConfig)
      syncPostHogIdentity(posthogConfig)
    })

    const tree = (
      <I18nextProvider i18n={i18n}>
        <App {...props} />
      </I18nextProvider>
    )

    if (el.hasChildNodes()) {
      hydrateRoot(el, tree)
      return
    }
    createRoot(el).render(<StrictMode>{tree}</StrictMode>)
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },

  progress: {
    color: "#4B5563",
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
    )
  }
})

// This will set light / dark mode on load...
initializeTheme()
